import React, { useState } from 'react';
import { Box, Table, TableBody, TableHead, TableRow, TableCell, FormControl, makeStyles, Theme, createStyles, Grid } from '@material-ui/core';
import { useTypedTranslation } from '@app/hooks';
import { PasslyTextField } from '@app/components';
import { PasslyTableRow } from '@app/componentsV2';
import { PasslyTableCell } from '@app/components/Table';
import { PasslyButton } from '@app/components/Buttons';
import { availableAttributes } from '@app/pages/ssoManager/addEditApplication.enums';
import { ICustomAttribute, ICustomClaims } from '@app/models';
import { Autocomplete } from '@material-ui/lab';
import { reservedClaims, defaultClaimsMapping } from '@app/helpers';

interface IProps {
    setCustomClaimsMapping: Function;
    formValidCallback: Function;
    customClaimsMapping: ICustomClaims;
}

export const renderReadOnlyRows = (item: ICustomAttribute, index: number) => {
    return (
        <PasslyTableRow key={index} >
            <PasslyTableCell>
                {item.Emit}
            </PasslyTableCell>
            <PasslyTableCell>
                <pre><code>{'=>'} </code></pre>
            </PasslyTableCell>
            <PasslyTableCell>
                {item.Type}
            </PasslyTableCell>
            <PasslyTableCell/>
        </PasslyTableRow>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        'custom-mapping': {
            maxWidth: '100%',
            overflow: 'auto',
            marginBottom: theme.spacing(2),
        },
        buttons: {
            '& > *': {
                marginRight: theme.spacing(1),
            },
        },
        'attribute-arrow': {
            padding: theme.spacing(2),
            textAlign: 'center',
            '& > *': {
                minWidth: theme.spacing(3),
            }
        },
    }),
);

const OpenIDConnectConfig: React.SFC<IProps> = ({ setCustomClaimsMapping, customClaimsMapping, formValidCallback }: IProps) => {

    const { translation } = useTypedTranslation();

    const classes = useStyles();

    const newClaimInitialState = {
        newClaimValue: '',
        newClaimName: ''
    };

    const [editIndex, setEditIndex] = useState<number>(-1);
    const [newClaim, setNewClaim] = useState(newClaimInitialState);
    const [invalidClaimName, setInvalidClaimName] = useState<boolean>(false);

    const onEdit = (index: number) => {
        setEditIndex(index);

        const editClaim = { newClaimValue: customClaimsMapping.Entries[index].Emit, newClaimName: customClaimsMapping.Entries[index].Type };
        setNewClaim(editClaim);
    };

    const onDelete = (index: number) => {
        const currentCustomClaims = { ...customClaimsMapping };
        currentCustomClaims.Entries.splice(index, 1);
        setCustomClaimsMapping(currentCustomClaims);
        formValidCallback(true);
    };

    const renderTableRows = (item: ICustomAttribute, index: number) => {
        return (
            <PasslyTableRow key={index}>
                <PasslyTableCell>
                    {item.Emit}
                </PasslyTableCell>
                <PasslyTableCell>
                    <pre><code>{'=>'}</code></pre>
                </PasslyTableCell>
                <PasslyTableCell>
                    {item.Type}
                </PasslyTableCell>
                <PasslyTableCell>
                    <PasslyTableCell>
                        <PasslyButton variant='contained' color='primary' onClick={() => onEdit(index)}>{translation('EDIT')}</PasslyButton>
                    </PasslyTableCell>
                    <PasslyTableCell>
                        <PasslyButton variant='contained' color='primary' onClick={() => onDelete(index)}>{translation('REMOVE')}</PasslyButton>
                    </PasslyTableCell>
                </PasslyTableCell>
            </PasslyTableRow>
        );
    };

    const cancelAddClaimMap = () => {
        setEditIndex(-1);
        setNewClaim(newClaimInitialState);
    };

    const addUpdateClaimMap = () => {
        const claimsMapping = { ...customClaimsMapping };
        const claim: ICustomAttribute = { Emit: newClaim.newClaimValue.trim(), Type: newClaim.newClaimName.trim(), Properties: {} };

        if (editIndex < 0) {
            // new claim
            claimsMapping.Entries = claimsMapping.Entries.concat([claim]);
        }
        else {
            // update claim
            claimsMapping.Entries[editIndex] = claim;
        }

        setCustomClaimsMapping(claimsMapping);
        formValidCallback(true);
        cancelAddClaimMap();
    };

    const handleNewClaimChange = (name: string, value: any) => {
        value = value === null ? '' : value;
        setNewClaim({ ...newClaim, [name]: value });

        if (name === 'newClaimName' && reservedClaims.includes(value.trim().toLowerCase())) {
            setInvalidClaimName(true);
        }
        else if (name === 'newClaimName') {
            setInvalidClaimName(false);
        }
    };

    return (
        <div className='rowcontainer'>
            <div className='configpanel-left configpanel-left--windows-logon'>
                <div className='container'>
                    <div className={classes['custom-mapping']}>
                        {(customClaimsMapping.Entries === undefined || customClaimsMapping.Entries?.length === 0) &&
                            <div className='empty-map-warning warning'>
                                <div className='label warning-text'><b>{translation('OIDC_NO_CUSTOM_CLAIMS_ADDED')}</b></div>
                            </div>
                        }
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translation('OIDC_CLAIM_VALUE')}</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>{translation('OIDC_CLAIM_NAME')}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <>
                                    {defaultClaimsMapping.map((item, index) => (
                                        renderReadOnlyRows(item, index)
                                    ))}
                                </>
                                <>
                                    {customClaimsMapping.Entries?.length > 0 && customClaimsMapping.Entries.map((item, index) => (
                                        renderTableRows(item, index)
                                    ))
                                    }
                                </>
                            </TableBody>
                        </Table>
                    </div>

                    <>
                        <Grid container direction='row' justifyContent='center'>
                            {/* ADDING ATTRIBUTE */}
                            <Grid item md={4}>
                                <Autocomplete
                                    id='newClaimValue'
                                    freeSolo
                                    fullWidth
                                    autoSelect
                                    options={availableAttributes.map((attribute) => attribute)}
                                    value={newClaim.newClaimValue}
                                    onChange={(event: any, inputValue: string | null) => handleNewClaimChange('newClaimValue', inputValue)}
                                    renderInput={(params) => (
                                        <PasslyTextField {...params} fullWidth label={translation('OIDC_CLAIM_VALUE')} helperText={translation('OIDC_CLAIM_VALUE_INPUT_HELPER')} />
                                    )}
                                />
                            </Grid>
                            <Grid item md={1}>
                                <FormControl className={classes['attribute-arrow']}>
                                    <Box pt={2} pl={2}>
                                        <code>{'=>'}</code>
                                    </Box>
                                </FormControl>
                            </Grid>
                            <Grid item md={7}>
                                <PasslyTextField
                                    id='newClaimName'
                                    fullWidth
                                    value={newClaim.newClaimName}
                                    onChange={(event: any) => handleNewClaimChange('newClaimName', event.target.value)}
                                    label={translation('OIDC_CLAIM_NAME')}
                                    error={invalidClaimName}
                                    helperText={invalidClaimName ? translation('OIDC_RESERVED_CLAIM_ERROR') : translation('OIDC_CLAIM_NAME_INPUT_HELPER')}
                                />
                            </Grid>
                        </Grid>
                        <Box className={classes.buttons} display='flex' justifyContent='flex-end'>
                            <PasslyButton variant='outlined' onClick={cancelAddClaimMap}>
                                {editIndex >= 0
                                    ?
                                    translation('CANCEL')
                                    :
                                    translation('CLEAR')
                                }</PasslyButton>
                            <PasslyButton variant='contained' color='primary' disabled={newClaim.newClaimValue === '' || newClaim.newClaimName === '' || invalidClaimName} onClick={addUpdateClaimMap}>
                                {editIndex >= 0
                                    ?
                                    translation('OIDC_UPDATE_CLAIM')
                                    :
                                    translation('OIDC_ADD_CLAIM')
                                }
                            </PasslyButton>
                        </Box>
                    </>
                </div>
            </div>
            <div className='configpanel-right configpanel-right--windows-logon'>
                <div dangerouslySetInnerHTML={{ __html: translation('OIDC_CONFIG_HELPER_HEADER') }} />
            </div>
        </div>
    );
};

export default OpenIDConnectConfig;